import type { TagProps } from '@launchpad-ui/components';

import type { Member, SamlConfig } from '../model';

export type AuthMethodCategory = 'SSO' | 'Marketplace' | 'OAuth' | 'Password' | '';

export type AuthMethodInfo = {
  label: string;
  category: AuthMethodCategory;
  variant: NonNullable<TagProps['variant']>;
  scim: boolean;
};

/**
 * Derives the IdP vendor display name from a SAML entity ID.
 * Patterns are based on production data from 2,250+ SSO accounts.
 */
export function deriveIdpVendor(entityId: string): string {
  if (entityId.startsWith('http://www.okta.com/') || entityId.startsWith('https://www.okta.com/')) {
    return 'Okta';
  }
  if (entityId.startsWith('https://sts.windows.net/')) {
    return 'Entra';
  }
  if (entityId.startsWith('https://accounts.google.com/')) {
    return 'Google';
  }
  if (entityId.includes('/realms/')) {
    return 'Keycloak';
  }
  if (entityId.startsWith('https://app.onelogin.com/') || entityId.includes('.onelogin.com/')) {
    return 'OneLogin';
  }
  if (entityId.startsWith('https://auth.pingone.com/')) {
    return 'PingOne';
  }
  if (entityId.startsWith('https://www.rippling.com/')) {
    return 'Rippling';
  }
  if (entityId.startsWith('JumpCloud')) {
    return 'JumpCloud';
  }
  if (entityId.includes('/adfs/services/trust')) {
    return 'ADFS';
  }
  if (entityId.includes('.cloudflareaccess.com')) {
    return 'Cloudflare';
  }
  if (entityId.includes('.duosecurity.com')) {
    return 'Duo';
  }

  // Fallback: extract domain from URL
  try {
    const url = new URL(entityId);
    const hostname = url.hostname.replace(/^www\./, '');
    // URN-style URLs (urn:saml2:...) parse with empty hostname
    if (hostname) {
      return hostname;
    }
    return entityId;
  } catch {
    // Not a URL (bare string) — return as-is
    return entityId;
  }
}

/**
 * Marketplace OAuth providers detected from member-level oauthProviders field.
 * Maps provider kind string to a human-readable label.
 */
const MARKETPLACE_OAUTH_PROVIDERS: Record<string, string> = {
  vercel_marketplace_oauth: 'Vercel',
};

/**
 * Account-level marketplace provider names (from subscription.marketplaceProvider).
 * Maps the display name to a badge label.
 */
const MARKETPLACE_ACCOUNT_PROVIDERS: Record<string, string> = {
  Vercel: 'Vercel',
};

/**
 * Derives the authentication method info for a member based on
 * account-level SSO config, account-level marketplace provider,
 * and member-level auth fields.
 *
 * Returns null for pending invite members (no auth method yet).
 *
 * Priority order (first match wins):
 * 1. SSO (account enforces requireSso — blocks ALL other login methods including OAuth)
 * 2. Marketplace (account is a marketplace account OR member has marketplace OAuth provider)
 * 3. OAuth (member has linked OAuth providers — only when SSO is NOT enforced)
 * 4. Password (member has basic auth — only when SSO is NOT enforced)
 * 5. Unknown (no auth signals — edge case)
 */
export function getAuthMethodInfo(
  member: Member,
  samlConfig?: SamlConfig,
  marketplaceProvider?: string,
): AuthMethodInfo | null {
  // Pending invite — member hasn't accepted yet, no auth method to show
  if (member._pendingInvite) {
    return null;
  }

  const scim = member.scimExternalId !== null && member.scimExternalId !== undefined && member.scimExternalId !== '';

  // 1. SSO — account enforces SSO, which blocks ALL other login methods (password AND OAuth).
  //    Matches gonfalon's SSOEnabledForAccount (saml.go:204-206): enabled AND requireSso.
  //    The login gate at service.go:147 redirects to the IdP before any credential check.
  if (samlConfig?.enabled && samlConfig?.requireSso) {
    let label: string;
    if (samlConfig.idpEntityId) {
      label = deriveIdpVendor(samlConfig.idpEntityId);
    } else {
      label = 'SSO';
    }
    return { label, category: 'SSO', variant: 'info', scim };
  }

  // 2. Marketplace — account is a marketplace account (primary signal from subscription),
  //    or member has a marketplace OAuth provider (fallback signal from member data).
  //    Marketplace members authenticate through the marketplace provider's SSO flow
  //    (e.g., Vercel SSO token exchange) and do not use the LD login page.
  if (marketplaceProvider) {
    const label = MARKETPLACE_ACCOUNT_PROVIDERS[marketplaceProvider] ?? marketplaceProvider;
    return { label, category: 'Marketplace', variant: 'beta', scim };
  }
  if (member.oauthProviders && member.oauthProviders.length > 0) {
    for (const provider of member.oauthProviders) {
      const marketplaceLabel = MARKETPLACE_OAUTH_PROVIDERS[provider];
      if (marketplaceLabel) {
        return { label: marketplaceLabel, category: 'Marketplace', variant: 'beta', scim };
      }
    }
  }

  // 3. OAuth — member has linked OAuth provider(s), only reachable when SSO is NOT enforced.
  //    Sort providers for deterministic display — Go's map iteration order is non-deterministic,
  //    so oauthProviders array order varies across API responses.
  if (member.oauthProviders && member.oauthProviders.length > 0) {
    const sorted = [...member.oauthProviders].sort();
    const provider = sorted[0];
    let label: string;
    if (provider === 'google_oauth') {
      label = 'Google';
    } else if (provider === 'github_oauth') {
      label = 'GitHub';
    } else {
      label = provider;
    }
    return { label, category: 'OAuth', variant: 'success', scim };
  }

  // 4. Password — basic auth, only reachable when SSO is NOT enforced
  if (member.hasPassword) {
    return { label: 'Basic Auth', category: 'Password', variant: 'warning', scim };
  }

  // 5. Unknown — no auth signals detected
  return { label: 'Unknown', category: '', variant: 'default', scim };
}

/**
 * Returns a description of stored credentials for a member.
 * Useful as tooltip text on SSO-enforced members to show what credentials
 * exist in the database (even though SSO blocks their use at login time).
 * Returns null if there are no stored credentials worth mentioning.
 */
export function getStoredCredentials(member: Member): string | null {
  const creds: string[] = [];

  if (member.oauthProviders && member.oauthProviders.length > 0) {
    const providers = [...member.oauthProviders].sort().map((p) => {
      if (p === 'google_oauth') {
        return 'Google OAuth';
      }
      if (p === 'github_oauth') {
        return 'GitHub OAuth';
      }
      if (p === 'vercel_marketplace_oauth') {
        return 'Vercel Marketplace OAuth';
      }
      return p;
    });
    creds.push(...providers);
  }

  if (member.hasPassword) {
    creds.push('Password');
  }

  if (creds.length === 0) {
    return null;
  }

  return `Stored credentials: ${creds.join(', ')}`;
}
